import { has, reduce, get, set, find } from 'lodash';
import moment from 'moment';
import PaymentHistory from '../entities/PaymentHistory';
import NumberUtils from '../utils/NumberUtils';
import config from '../config';
import {
  TYPE_IFT,
  TYPE_CHAPS,
  CONST_GBP_EQUIVALENT,
  CONST_AMOUNT_UPPERCASE,
  CONST_INSTRUCTED,
  CONST_EQUIVALENT,
} from '../constants/StringConstants';
export const parsePaymentHistoryResponse = (response) => {
  let transactionList = reduce(
    get(response, 'transactions', []),
    (transactionList, transaction) => {
      let paymentHistory = new PaymentHistory();
      if (has(transaction, 'batch_transactions')) {
        paymentHistory = parseBatchList(transaction);
      } else {
        paymentHistory = parseSinglePayment(transaction);
      }
      transactionList.push(paymentHistory);
      return transactionList;
    },
    []
  );
  transactionList = reduce(
    get(response, 'bacs', []),
    (transactionList, transaction) => {
      const paymentHistory = parseBacsPayment(transaction);
      transactionList.push(paymentHistory);
      return transactionList;
    },
    transactionList
  );
  transactionList = reduce(
    get(response, 'ift', []),
    (transactionList, transaction) => {
      const IFTPaymentHistory = parseIFTPayment(transaction);
      transactionList.push(IFTPaymentHistory);
      return transactionList;
    },
    transactionList
  );
  transactionList = reduce(
    get(response, 'chaps', []),
    (transactionList, transaction) => {
      let chapsPaymentHistory = new PaymentHistory();
      if (has(transaction, 'chaps_batch_transactions')) {
        chapsPaymentHistory = parseChapsBatchList(transaction);
      } else {
        chapsPaymentHistory = parseChapsPayment(transaction);
      }
      transactionList.push(chapsPaymentHistory);
      return transactionList;
    },
    transactionList
  );
  return transactionList;
};

export const parseSinglePayment = (transaction) => ({
  paymentType: config.fasterPayments,
  id: transaction.id,
  status: transaction.status,
  createdDateRaw: transaction.last_updated_on,
  createdDate: moment(transaction.last_updated_on).format(
    config.datetimeFormatPaymentHistory
  ),
  fromAccountName: transaction.from_account.name,
  fromAccountSortCode: NumberUtils.formatSortCode(
    get(transaction, 'from_account.sort_code', '')
  ),
  fromAccountAccountNumber: transaction.from_account.account_number,
  fromAccountAccountUUID: transaction.from_account.account_uuid,
  toAccountName: transaction.to_account.name,
  toAccountSortCode: NumberUtils.formatSortCode(
    get(transaction, 'to_account.sort_code', '')
  ),
  toAccountAccountNumber: transaction.to_account.account_number,
  toAccountAccountUUID: transaction.to_account.account_uuid,
  reference: transaction.reference,
  amount: NumberUtils.appendCurrency('{}', transaction.amount.value),
  amountRaw: transaction.amount.value,
  statusCode: transaction.result_code,
  createdBy: get(transaction, 'created_by', null),
  authorisedBy: get(transaction, 'approvers', []).join(' | '),
});

export const parseBacsChildren = (entries, type) => {
  return entries.map((entry) => {
    const child = {
      id: entry.id,
      reference: entry.reference,
      transactionCode: entry.transaction_code,
      amount: NumberUtils.appendCurrency('{}', entry.amount.value),
      hmrcReference: entry.hmrc_reference,
    };
    if (type === 'BCR') {
      child.toAccountName = entry.account.name;
      child.toAccountSortCode = NumberUtils.formatSortCode(
        get(entry, 'account.sort_code', '')
      );
      child.toAccountAccountNumber = entry.account.account_number;
      child.toAccountAccountUUID = entry.account.account_uuid;
    } else {
      child.fromAccountName = entry.account.name;
      child.fromAccountSortCode = NumberUtils.formatSortCode(
        get(entry, 'account.sort_code', '')
      );
      child.fromAccountAccountNumber = entry.account.account_number;
      child.fromAccountAccountUUID = entry.account.account_uuid;
    }
    return child;
  });
};

export const parseBacsPayment = ({
  id,
  bacs_id,
  type,
  bacs_name,
  batch_type,
  total: { value },
  created_by: createdBy,
  status,
  result_code,
  settlement_date,
  submission_date,
  last_updated_on,
  approvers = [],
  entries = [],
  account: { name, sort_code, account_number, account_uuid },
}) => {
  const paymentHistory = {
    id,
    bacsId: bacs_id,
    type: type,
    bacsName: bacs_name,
    amount: NumberUtils.appendCurrency('{}', value),
    amountRaw: value,
    createdBy,
    status: status,
    statusCode: result_code,
    settlement_date,
    submission_date,
    createdDateRaw: last_updated_on,
    createdDate: moment(last_updated_on).format(
      config.datetimeFormatPaymentHistory
    ),
    authorisedBy: approvers.join(' | '),
    batchType: batch_type,
  };

  if (paymentHistory.type === 'BCR') {
    paymentHistory.paymentType = config.bacsCredit;
    paymentHistory.fromAccountName = name;
    paymentHistory.fromAccountSortCode = NumberUtils.formatSortCode(sort_code);
    paymentHistory.fromAccountAccountNumber = account_number;
    paymentHistory.fromAccountAccountUUID = account_uuid;
    paymentHistory.toAccountName = bacs_name;
  } else {
    paymentHistory.paymentType = config.bacsDebit;
    paymentHistory.toAccountName = name;
    paymentHistory.toAccountSortCode = NumberUtils.formatSortCode(sort_code);
    paymentHistory.toAccountAccountNumber = account_number;
    paymentHistory.toAccountAccountUUID = account_uuid;
    paymentHistory.fromAccountName = bacs_name;
  }
  paymentHistory.children = parseBacsChildren(entries, type);
  return paymentHistory;
};

const parseBatchList = (batch) => {
  const paymentHistory = {
    paymentType: config.fasterPayments,
    id: batch.batch_id,
    status: batch.batch_status,
    toAccountName: batch.batch_name,
    createdDateRaw: batch.last_updated_on,
    createdDate: moment(batch.last_updated_on).format(
      config.datetimeFormatPaymentHistory
    ),
    amount: NumberUtils.appendCurrency('{}', batch.batch_amount.value),
    amountRaw: batch.batch_amount.value,
    fromAccountName: get(batch, 'from_account.name', ''),
    fromAccountSortCode: NumberUtils.formatSortCode(
      get(batch, 'from_account.sort_code', '')
    ),
    fromAccountAccountNumber: get(batch, 'from_account.account_number', ''),
    fromAccountAccountUUID: get(batch, 'from_account.account_uuid', ''),
    batchType: get(batch, 'batch_type', ''),
  };

  const batchList = [];
  batch.batch_transactions.map((transaction) => {
    const batchPayment = {};
    batchPayment.id = transaction.id;
    batchPayment.status = transaction.status;
    batchPayment.fromAccountName = get(transaction, 'from_account.name', '');
    batchPayment.fromAccountSortCode = NumberUtils.formatSortCode(
      get(transaction, 'from_account.sort_code', '')
    );
    batchPayment.fromAccountAccountNumber = get(
      transaction,
      'from_account.account_number',
      ''
    );
    batchPayment.toAccountName = transaction.to_account.name;
    batchPayment.toAccountSortCode = NumberUtils.formatSortCode(
      get(transaction, 'to_account.sort_code', '')
    );
    batchPayment.toAccountAccountNumber = transaction.to_account.account_number;
    batchPayment.toAccountAccountUUID = transaction.to_account.account_uuid;
    batchPayment.reference = transaction.reference;
    batchPayment.amount = NumberUtils.appendCurrency(
      '{}',
      transaction.amount.value
    );
    batchPayment.amountRaw = transaction.amount.value;
    batchPayment.createdDate = moment(transaction.last_updated_on).format(
      config.datetimeFormatPaymentHistory
    );
    batchPayment.statusCode = transaction.result_code;
    batchPayment.createdBy = get(transaction, 'created_by', null);
    batchPayment.authorisedBy = get(transaction, 'approvers', []).join(' | ');
    batchList.push(batchPayment);
    return true;
  });
  paymentHistory.children = batchList;
  return paymentHistory;
};

export const parseChapsBatchList = (batch) => {
  const paymentHistory = {
    type: TYPE_CHAPS,
    paymentType: config.chaps,
    id: batch.batch_id,
    status: batch.batch_status,
    toAccountName: batch.batch_name,
    createdDateRaw: batch.last_updated_on,
    createdDate: moment(batch.last_updated_on).format(
      config.datetimeFormatPaymentHistory
    ),
    authorisedBy: get(batch, 'approvers', []).join(' | '),
    amount: NumberUtils.appendCurrency('{}', batch.batch_amount.value),
    amountRaw: batch.batch_amount.value,
    createdBy: get(batch, 'created_by', null),
    fromAccountName: get(batch, 'from_account.name', ''),
    fromAccountSortCode: NumberUtils.formatSortCode(
      get(batch, 'from_account.sort_code', '')
    ),
    fromAccountAccountNumber: get(batch, 'from_account.account_number', ''),
    fromAccountAccountUUID: get(batch, 'from_account.account_uuid', ''),
    submissionDate: moment(batch.submission_date).format(
      config.datetimeFormatPaymentHistory
    ),
    settlementDate: moment(batch.value_date).format(
      config.datetimeFormatPaymentHistory
    ),
    batchType: get(batch, 'batch_type', ''),
  };
  const chapsBatchFees = (chargeAmount) => {
    const chapsAmountFees = chargeAmount.find(
      (amount) => amount.type === CONST_AMOUNT_UPPERCASE
    );
    const amount = chapsAmountFees
      ? chapsAmountFees
      : chargeAmount.find((amount) => amount.type === CONST_GBP_EQUIVALENT);
    return NumberUtils.appendCurrency('{}', get(amount, 'value', ''));
  };

  const batchList = get(batch, 'chaps_batch_transactions', []).map(
    (transaction) => ({
      id: transaction.id,
      status: transaction.status,
      toAccountName: transaction.to_account.name,
      toAccountSortCode: NumberUtils.formatSortCode(
        get(transaction, 'to_account.sort_code', '')
      ),
      toAccountAccountNumber: get(transaction, 'to_account.account_number', ''),
      toAccountAccountUUID: get(transaction, 'to_account.account_uuid', ''),
      reference: transaction.reference,
      amount: NumberUtils.appendCurrency('{}', transaction.amount.value),
      amountRaw: transaction.amount.value,
      createdDate: moment(transaction.last_updated_on).format(
        config.datetimeFormatPaymentHistory
      ),
      statusCode: transaction.result_code,
      paymentFees: chapsBatchFees(get(transaction, 'charge_amount', [])),
    })
  );
  return { ...paymentHistory, children: batchList };
};

export const parseIFTPayment = ({
  id,
  to_account: { name, account_number, beneficiary_country = '' } = {},
  from_account = {},
  status,
  payment_amount,
  charge_amount,
  submission_date,
  indicative_fx: { rate = '', type = '' } = {},
  actual_fx,
  value_date,
  reference,
  created_by,
  approvers = [],
  charging_model,
  result_code,
  last_updated_on,
  deal_reference,
}) => {
  const IFTPaymentHistory = {
    type: TYPE_IFT,
    paymentType: config.international,
    id,
    createdDate: moment(last_updated_on).format(
      config.datetimeFormatPaymentHistory
    ),
    createdDateRaw: last_updated_on,
    fromAccountName: get(from_account, 'name', ''),
    fromAccountSortCode: NumberUtils.formatSortCode(
      get(from_account, 'sort_code', '')
    ),
    fromAccountAccountNumber: from_account.account_number,
    fromAccountAccountUUID: from_account.account_uuid,
    toAccountName: name,
    toAccountAccountNumber: account_number,
    beneficiaryCountry: beneficiary_country,
    status: status,
    paymentFees: getPaymentFees(charge_amount, TYPE_IFT),
    submissionDate: moment(submission_date).format(
      config.datetimeFormatPaymentHistory
    ),
    indicativeExchangeRate: rate,
    actualExchangeRate: get(actual_fx, 'rate', ''),
    ...getIFTAmount(payment_amount, from_account.currency, charge_amount),
    settlementDate: moment(value_date).format(
      config.datetimeFormatPaymentHistory
    ),
    reference: reference,
    createdBy: created_by,
    authorisedBy: approvers.join(' | '),
    chargingModal: charging_model,
    statusCode: result_code,
    deal_reference,
    indicativeRateType: type,
    actualRateType: get(actual_fx, 'type', ''),
  };
  return IFTPaymentHistory;
};

export const parseChapsPayment = ({
  id,
  reference,
  status,
  result_code,
  created_by,
  submission_date,
  amount: { value },
  from_account,
  to_account: { account_number, name, sort_code },
  charge_amount = [],
  value_date,
  approvers = [],
  created_timestamp,
}) => {
  const chapsPaymentHistory = {
    type: TYPE_CHAPS,
    paymentType: config.chaps,
    id,
    createdDate: moment(created_timestamp).format(
      config.datetimeFormatPaymentHistory
    ),
    createdDateRaw: created_timestamp,
    fromAccountName: get(from_account, 'name', ''),
    fromAccountSortCode: NumberUtils.formatSortCode(
      get(from_account, 'sort_code', '')
    ),
    fromAccountAccountNumber: from_account.account_number,
    fromAccountAccountUUID: from_account.account_uuid,
    toAccountName: name,
    toAccountAccountNumber: account_number,
    toAccountSortCode: NumberUtils.formatSortCode(sort_code),
    submissionDate: moment(submission_date).format(
      config.datetimeFormatPaymentHistory
    ),
    settlementDate: moment(value_date).format(
      config.datetimeFormatPaymentHistory
    ),
    status: status,
    paymentFees: getPaymentFees(charge_amount, TYPE_CHAPS),
    amount: NumberUtils.appendCurrency('{}', value),
    reference: reference,
    createdBy: created_by,
    authorisedBy: approvers.join(' | '),
    statusCode: result_code,
  };
  return chapsPaymentHistory;
};
const getCurrencySymbol = (currency) =>
  ({ USD: '$', EUR: '€' }[currency] || '');

export const getPaymentFees = (paymentAmount, paymentType) => {
  const amountFees = find(paymentAmount, { type: CONST_AMOUNT_UPPERCASE });
  const amount = amountFees
    ? amountFees
    : find(paymentAmount, { type: CONST_GBP_EQUIVALENT });
  const currency = get(amount, 'currency', '');
  if (paymentType === TYPE_CHAPS) {
    return NumberUtils.appendCurrency('{}', get(amount, 'value', ''));
  }
  return NumberUtils.appendCurrency('{}', get(amount, 'value', ''), currency);
};

export const getIFTAmount = (
  paymentAmount,
  fromCurrency,
  charge_amount,
  isChargeToRecipient
) => {
  const amountFees = find(charge_amount, { type: CONST_AMOUNT_UPPERCASE });
  const chargeAmount = amountFees
    ? get(amountFees, 'value', 0)
    : get(find(charge_amount, { type: CONST_GBP_EQUIVALENT }), 'value', 0);

  const instructedAmount = find(paymentAmount, { type: CONST_INSTRUCTED });
  const equivalentAmount = find(paymentAmount, { type: CONST_EQUIVALENT });
  const instructedCurrency = get(instructedAmount, 'currency', '');
  const equivalentCurrency = get(equivalentAmount, 'currency', '');

  const formatInstructedAmt = NumberUtils.appendCurrency(
    '{}',
    get(instructedAmount, 'value', ''),
    instructedCurrency,
    undefined,
    true
  );
  const formatEquivalentAmt = NumberUtils.appendCurrency(
    '{}',
    get(equivalentAmount, 'value', ''),
    equivalentCurrency,
    undefined,
    true
  );

  if (instructedAmount.currency === fromCurrency) {
    return {
      debitAmount: `${getCurrencySymbol(
        instructedCurrency
      )}${formatInstructedAmt}`,
      amount: `${getCurrencySymbol(equivalentCurrency)}${formatEquivalentAmt}`,
      amountRaw: get(equivalentAmount, 'value', ''),
      amountCurrency: equivalentCurrency,
      chargeAmount,
    };
  }
  return {
    debitAmount: `${getCurrencySymbol(
      equivalentCurrency
    )}${formatEquivalentAmt}`,
    amount: `${getCurrencySymbol(instructedCurrency)}${formatInstructedAmt}`,
    amountRaw: get(instructedAmount, 'value', ''),
    amountCurrency: instructedCurrency,
    chargeAmount,
  };
};
const getRequestFromDate = (filterCriteria) => {
  if (filterCriteria.isCustomDateInterval) {
    return filterCriteria.fromDate;
  } else {
    const splittedDateInterval = filterCriteria.dateInterval.split(' ');
    return moment(filterCriteria.toDate)
      .subtract(splittedDateInterval[0], splittedDateInterval[1])
      .add(1, 'day');
  }
};

export const createGetPaymentHistoryRequest = (filterCriteria, accounts) => ({
  params: {
    start_date: getRequestFromDate(filterCriteria).startOf('day').toISOString(),
    end_date: filterCriteria.toDate.endOf('day').toISOString(),
    account_list: accounts,
    status: filterCriteria.status,
  },
});
export const createGetPaymentHistoryRequestForCreator = (
  filterCriteria,
  accounts
) => {
  const requestPacket = createGetPaymentHistoryRequest(
    filterCriteria,
    accounts
  );
  set(requestPacket, 'params.user_search_type', 'creator');
  return requestPacket;
};
